import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import {
  apiGetPatientsByPatientIdDiagnosesOptions,
  apiGetPatientsByPatientIdThresholdsOptions,
  apiGetThresholdsDefaultOptions,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { hasMedicalAccess } from "../../../constants/permissions";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { assert } from "../../../utils/utils";
import { PatientSubpage } from "../common/patient-subpage";
import { AddThresholdsDialog } from "./add-thresholds-dialog";
import { SuppressionCell } from "./suppression-cell";
import { Thresholds } from "./thresholds";

export const ThresholdsTable = observer(function ThresholdsTable(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [model] = useState(
    () => new Thresholds([], [], api.permissions, api.requireUserUUID()),
  );
  const [showAddDialog, setShowAddDialog] = useState(false);
  const defaultQuery = useQuery(apiGetThresholdsDefaultOptions());
  const thresholdsQuery = useQuery(
    apiGetPatientsByPatientIdThresholdsOptions({
      path: { patient_id: patient.patientId },
    }),
  );
  // Diagnoses only mark which default thresholds are suppressed. Thresholds sit
  // behind VIEW_THRESHOLDS but diagnoses behind medical access, so this is
  // best-effort: without them the table still lists each default's
  // suppressing comorbidities, just none marked as active.
  const canReadDiagnoses = hasMedicalAccess(api.permissions);
  const diagnosesQuery = useQuery({
    ...apiGetPatientsByPatientIdDiagnosesOptions({
      path: { patient_id: patient.patientId },
    }),
    enabled: canReadDiagnoses,
    meta: { handlesOwnErrors: true },
  });
  const loading =
    defaultQuery.isPending ||
    thresholdsQuery.isPending ||
    diagnosesQuery.isLoading;
  const refetch = async () => {
    await Promise.all([
      defaultQuery.refetch(),
      thresholdsQuery.refetch(),
      // `refetch` ignores `enabled`, so it has to be gated here as well.
      canReadDiagnoses && diagnosesQuery.refetch(),
    ]);
  };

  useEffect(() => {
    if (!defaultQuery.data || !thresholdsQuery.data || diagnosesQuery.isLoading)
      return;
    model.defaultThresholds = defaultQuery.data.thresholds;
    model.diagnoses = diagnosesQuery.data?.diagnoses ?? [];
    model.updateData(thresholdsQuery.data.thresholds);
  }, [
    defaultQuery.data,
    thresholdsQuery.data,
    diagnosesQuery.data,
    diagnosesQuery.isLoading,
    model,
  ]);

  return (
    <PatientSubpage title={t`Thresholds`} loading={loading}>
      <DataTable
        filterActions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setShowAddDialog(true)}
          >
            <Trans>Add thresholds</Trans>
          </Button>
        }
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="key"
        filterNoun={(count) =>
          t`${plural(count, { one: "threshold", other: "thresholds" })}`
        }
        columns={[
          {
            header: <Trans>Variable table</Trans>,
            attr: "threshold_variable_table",
            type: "enum",
            values: model.threshTables,
            filter: {
              kind: "multi",
              group: t`Threshold`,
              options: new Map(Object.entries(model.threshTables)),
            },
          },
          {
            header: <Trans>Variable</Trans>,
            attr: "threshold_variable",
            type: "string",
          },
          {
            header: <Trans>Trigger</Trans>,
            attr: "threshold_trigger",
            type: "enum",
            values: model.threshTriggers,
            filter: {
              kind: "multi",
              group: t`Threshold`,
              options: new Map(Object.entries(model.threshTriggers)),
            },
          },
          {
            header: <Trans>Type</Trans>,
            attr: "threshold_type",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Threshold`,
              options: uniqueValueOptions("threshold_type"),
            },
          },
          {
            header: <Trans>Value</Trans>,
            attr: "threshold_value",
            type: "number",
            editable: true,
          },
          {
            header: <Trans>How</Trans>,
            cell: ({ item }) => model.settingsString(item),
          },
          {
            header: <Trans>Comment</Trans>,
            type: "string",
            attr: "threshold_comment",
            multiline: true,
            editable: true,
          },
          {
            header: <Trans>Suppressed by</Trans>,
            width: 250,
            cell: ({ item }) => <SuppressionCell model={model} item={item} />,
          },
        ]}
        // A suppressed default doesn't apply to this patient, so it's faded
        // out the way the app dims other inactive rows. Opacity (rather than a
        // muted text colour) also survives the zebra striping and the sticky
        // Edit/Delete cells, which paint their own backgrounds.
        rowClassName={(item) =>
          model.isSuppressed(item) ? "opacity-50" : undefined
        }
        onEdit={async (editedField, item) => {
          assert(item.uuid, "row is missing its uuid");
          await patient.updateItem("thresholds", item.uuid, editedField);
          await refetch();
        }}
        onDelete={async (item) => {
          assert(item.uuid, "row is missing its uuid");
          await patient.deleteItem("thresholds", item.uuid);
          await refetch();
        }}
      />

      {showAddDialog && (
        <AddThresholdsDialog
          app={props.app}
          defaultThresholds={model.defaultThresholds ?? []}
          onClose={() => setShowAddDialog(false)}
          onSaved={refetch}
        />
      )}
    </PatientSubpage>
  );
});
